<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { useNavigationStore } from '../../stores/navigationStore'

const props = defineProps<{
  labelKey: string   // e.g. "mg:5", "sg:5:10", "cat:42"
  fallback: string   // raw TecDoc name used when no override exists
}>()

const navStore = useNavigationStore()
const editing = ref(false)
const draft = ref('')
const saving = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)

const displayName = computed(() => navStore.getDisplayName(props.labelKey, props.fallback))
const canEdit = computed(() => navStore.canEditCategories)

async function beginEdit(e: Event) {
  e.stopPropagation()
  e.preventDefault()
  draft.value = displayName.value
  editing.value = true
  await nextTick()
  inputRef.value?.focus()
  inputRef.value?.select()
}

async function save(e?: Event) {
  e?.stopPropagation()
  e?.preventDefault()
  if (saving.value) return
  const next = draft.value.trim()
  // Save null to clear override back to the raw TecDoc name, else save the new label.
  const toSave = next === '' || next === props.fallback ? null : next
  saving.value = true
  try {
    await navStore.updateCategoryName(props.labelKey, toSave)
    editing.value = false
  } catch {
    // leave the input open so the user can retry
  } finally {
    saving.value = false
  }
}

function cancel(e?: Event) {
  e?.stopPropagation()
  e?.preventDefault()
  editing.value = false
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') save(e)
  else if (e.key === 'Escape') cancel(e)
}
</script>

<template>
  <span class="editable-label">
    <template v-if="!editing">
      <button
        v-if="canEdit"
        class="editable-label__pencil"
        title="Rediger navn"
        @click="beginEdit"
        @mousedown.stop
      >
        &#9998;
      </button>
      <span class="editable-label__text">{{ displayName }}</span>
    </template>
    <template v-else>
      <input
        ref="inputRef"
        v-model="draft"
        class="editable-label__input"
        type="text"
        :disabled="saving"
        @keydown="onKeydown"
        @click.stop
        @mousedown.stop
        @blur="save"
      />
    </template>
  </span>
</template>

<style scoped lang="scss">
.editable-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;

  &__pencil {
    background: transparent;
    border: none;
    padding: 0 4px;
    cursor: pointer;
    color: #666;
    font-size: 0.85em;
    line-height: 1;
    border-radius: 3px;
    flex-shrink: 0;

    &:hover {
      color: #002868;
      background: rgba(0, 40, 104, 0.08);
    }
  }

  &__text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__input {
    flex: 1;
    min-width: 0;
    padding: 2px 6px;
    border: 1px solid #002868;
    border-radius: 4px;
    font-size: inherit;
    font-family: inherit;
    background: white;
    color: #222;

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(0, 40, 104, 0.2);
    }
  }
}
</style>
